import { useEffect, useRef, useState } from 'react';

// Drives the thin top progress bar. Returns a 0–1 scale value for scaleX.
export function useScrollProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(1, window.scrollY / h) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return progress;
}

// Soft cursor-follower ring (fine pointers only). It does not hide the native cursor;
// it grows and tints when hovering interactive elements. Self-contained: mounts its
// own DOM node and cleans up on unmount.
export function useCursorFollower() {
  const raf = useRef(null);

  useEffect(() => {
    const isFine = window.matchMedia && window.matchMedia('(pointer:fine)').matches;
    if (!isFine) return undefined;

    const ring = document.createElement('div');
    ring.style.cssText =
      'position:fixed;top:0;left:0;width:34px;height:34px;margin:-17px 0 0 -17px;' +
      'border:1.5px solid rgba(77,141,255,.7);border-radius:50%;pointer-events:none;' +
      'z-index:190;transition:width .25s,height .25s,margin .25s,background .25s,opacity .3s;opacity:0;';
    document.body.appendChild(ring);

    let tx = 0, ty = 0, cx = 0, cy = 0, seen = false;

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
      if (!seen) {
        cx = tx;
        cy = ty;
        seen = true;
        ring.style.opacity = '1';
      }
      const hot =
        e.target &&
        e.target.closest &&
        e.target.closest('a,button,[data-magnetic],input,select,textarea');
      if (hot) {
        ring.style.width = '58px';
        ring.style.height = '58px';
        ring.style.margin = '-29px 0 0 -29px';
        ring.style.background = 'rgba(77,141,255,.1)';
      } else {
        ring.style.width = '34px';
        ring.style.height = '34px';
        ring.style.margin = '-17px 0 0 -17px';
        ring.style.background = 'transparent';
      }
    };
    window.addEventListener('mousemove', onMove, { passive: true });

    const loop = () => {
      cx += (tx - cx) * 0.18;
      cy += (ty - cy) * 0.18;
      ring.style.transform = `translate(${cx}px,${cy}px)`;
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf.current) cancelAnimationFrame(raf.current);
      ring.remove();
    };
  }, []);
}
